// FILE: threadBootstrap.ts
// Purpose: Pure helpers for draft reuse and terminal-thread promotion payloads.
// Layer: Web bootstrap/domain helpers
// Exports: draft patching, reuse checks, and terminal creation state resolution.

import {
  DEFAULT_RUNTIME_MODE,
  type ModelSelection,
  type ProjectId,
  type ProviderInteractionMode,
  type ProviderKind,
  type RuntimeMode,
  type ThreadEnvironmentMode,
  type ThreadId,
} from "@taskin-code/contracts";
import type { ModelSelectionPolicy } from "../allowedModelSelection";
import {
  type ComposerThreadDraftState,
  type DraftPlaneIssue,
  type DraftPullRequest,
  type DraftThreadEnvMode,
  type DraftThreadState,
  resolvePreferredComposerModelSelection,
} from "../composerDraftStore";
import { DEFAULT_INTERACTION_MODE, type ThreadPrimarySurface } from "../types";

export interface NewThreadOptions {
  branch?: string | null;
  worktreePath?: string | null;
  envMode?: DraftThreadEnvMode;
  entryPoint?: ThreadPrimarySurface;
  temporary?: boolean;
  provider?: ProviderKind;
  /** Plane issue this thread is being opened for, when it starts from one. */
  planeIssue?: DraftPlaneIssue | null;
  /** Pull request this thread is being opened for, when it starts from one. */
  pullRequest?: DraftPullRequest | null;
}

interface ActiveThreadSnapshot {
  projectId: ProjectId;
  modelSelection: ModelSelection;
  runtimeMode: RuntimeMode;
  interactionMode: ProviderInteractionMode;
  envMode?: ThreadEnvironmentMode | undefined;
}

export interface DraftReusePlanStored {
  draftThread: DraftThreadState;
  kind: "stored";
  threadId: ThreadId;
}

export interface DraftReusePlanRoute {
  draftThread: DraftThreadState;
  kind: "route";
  threadId: ThreadId;
}

export interface DraftReusePlanFresh {
  kind: "fresh";
}

export type ThreadBootstrapPlan = DraftReusePlanStored | DraftReusePlanRoute | DraftReusePlanFresh;

interface ResolveTerminalThreadCreationStateInput {
  activeDraftThread: DraftThreadState | null;
  activeThread: ActiveThreadSnapshot | null;
  defaultProvider?: ProviderKind | null | undefined;
  draftComposerState: ComposerThreadDraftState | null;
  draftThread: DraftThreadState | null;
  options: NewThreadOptions | undefined;
  projectDefaultModelSelection: ModelSelection | null;
  userDefaultModelSelection?: ModelSelection | null | undefined;
  /** The caller's model/agent policy, when known. See `resolvePreferredComposerModelSelection`. */
  modelSelectionPolicy?: ModelSelectionPolicy | null | undefined;
  projectId: ProjectId;
  projectPlanModeByDefault?: boolean;
}

export interface TerminalThreadCreationState {
  branch: string | null;
  envMode: DraftThreadEnvMode;
  interactionMode: ProviderInteractionMode;
  modelSelection: ModelSelection;
  runtimeMode: RuntimeMode;
  worktreePath: string | null;
}

// Normalize the currently active server thread into a stable snapshot for pure helpers.
export function createActiveThreadSnapshot(
  activeThread:
    | {
        interactionMode: ProviderInteractionMode;
        modelSelection: ModelSelection;
        projectId: ProjectId;
        runtimeMode: RuntimeMode;
        envMode?: ThreadEnvironmentMode | undefined;
      }
    | null
    | undefined,
  projectId: ProjectId,
): ActiveThreadSnapshot | null {
  if (!activeThread || activeThread.projectId !== projectId) {
    return null;
  }
  return {
    projectId: activeThread.projectId,
    modelSelection: activeThread.modelSelection,
    runtimeMode: activeThread.runtimeMode,
    interactionMode: activeThread.interactionMode,
    envMode: activeThread.envMode,
  };
}

// Normalize the currently active draft thread into a stable snapshot for pure helpers.
export function createActiveDraftThreadSnapshot(
  activeDraftThread: DraftThreadState | null | undefined,
  projectId: ProjectId,
): DraftThreadState | null {
  if (!activeDraftThread || activeDraftThread.projectId !== projectId) {
    return null;
  }
  return {
    projectId: activeDraftThread.projectId,
    createdAt: activeDraftThread.createdAt,
    runtimeMode: activeDraftThread.runtimeMode,
    interactionMode: activeDraftThread.interactionMode,
    entryPoint: activeDraftThread.entryPoint,
    branch: activeDraftThread.branch,
    worktreePath: activeDraftThread.worktreePath,
    envMode: activeDraftThread.envMode,
    ...(activeDraftThread.isTemporary ? { isTemporary: true } : {}),
  };
}

// Decide whether we should reuse a stored draft, the current route draft, or create a fresh one.
export function resolveThreadBootstrapPlan(input: {
  entryPoint: ThreadPrimarySurface;
  latestActiveDraftThread: DraftThreadState | null;
  projectId: ProjectId;
  routeThreadId: ThreadId | null;
  storedDraftThread: ({ threadId: ThreadId } & DraftThreadState) | null;
}): ThreadBootstrapPlan {
  if (
    shouldReuseActiveDraftThread({
      draftThread: input.latestActiveDraftThread,
      entryPoint: input.entryPoint,
      projectId: input.projectId,
      routeThreadId: input.routeThreadId,
    })
  ) {
    return {
      kind: "route",
      threadId: input.routeThreadId!,
      draftThread: input.latestActiveDraftThread!,
    };
  }
  if (input.storedDraftThread) {
    return {
      kind: "stored",
      threadId: input.storedDraftThread.threadId,
      draftThread: input.storedDraftThread,
    };
  }
  return { kind: "fresh" };
}

// Build the initial draft-thread metadata for a brand new thread bootstrap.
export function createFreshDraftThreadSeed(input: {
  createdAt: string;
  entryPoint: ThreadPrimarySurface;
  options: NewThreadOptions | undefined;
}): Omit<DraftThreadState, "projectId" | "interactionMode"> {
  return {
    createdAt: input.createdAt,
    branch: input.options?.branch ?? null,
    worktreePath: input.options?.worktreePath ?? null,
    envMode: input.options?.envMode ?? "local",
    runtimeMode: DEFAULT_RUNTIME_MODE,
    entryPoint: input.entryPoint,
    ...(input.options?.temporary ? { isTemporary: true } : {}),
    planeIssue: input.options?.planeIssue ?? null,
    pullRequest: input.options?.pullRequest ?? null,
  };
}

// Detect whether the caller wants to override stored draft context before reuse.
export function hasDraftContextOverrides(options?: NewThreadOptions): boolean {
  return (
    options?.branch !== undefined ||
    options?.worktreePath !== undefined ||
    options?.envMode !== undefined ||
    // Unlike `planeIssue`, which only ever arrives with a brand new draft from the New Thread
    // dialog, a pull request can land on a draft that already exists: "Checkout Pull Request"
    // reuses the project's open draft when there is one. Without this the link would be dropped
    // exactly in the case the feature is for.
    options?.pullRequest !== undefined
  );
}

// Build the exact patch we should apply to an existing draft before reusing it.
export function buildDraftThreadContextPatch(
  entryPoint: ThreadPrimarySurface,
  options?: NewThreadOptions,
): {
  branch?: string | null;
  entryPoint: ThreadPrimarySurface;
  envMode?: DraftThreadEnvMode;
  worktreePath?: string | null;
  pullRequest?: DraftPullRequest | null;
} | null {
  if (!hasDraftContextOverrides(options)) {
    return null;
  }
  const shouldClearWorktreeForLocalMode =
    options?.envMode === "local" && options?.worktreePath === undefined;
  return {
    ...(options?.branch !== undefined ? { branch: options.branch ?? null } : {}),
    ...(options?.worktreePath !== undefined || shouldClearWorktreeForLocalMode
      ? { worktreePath: options?.worktreePath ?? null }
      : {}),
    ...(options?.envMode !== undefined ? { envMode: options.envMode } : {}),
    ...(options?.pullRequest !== undefined ? { pullRequest: options.pullRequest } : {}),
    entryPoint,
  };
}

// Reuse only when the active route draft already belongs to the target project and surface.
export function shouldReuseActiveDraftThread(input: {
  draftThread: DraftThreadState | null;
  entryPoint: ThreadPrimarySurface;
  projectId: ProjectId;
  routeThreadId: ThreadId | null;
}): input is {
  draftThread: DraftThreadState;
  entryPoint: ThreadPrimarySurface;
  projectId: ProjectId;
  routeThreadId: ThreadId;
} {
  return Boolean(
    input.draftThread &&
    input.routeThreadId &&
    input.draftThread.projectId === input.projectId &&
    input.draftThread.entryPoint === input.entryPoint,
  );
}

// Resolve the durable thread payload for terminal-first promotion from the most specific state.
export function resolveTerminalThreadCreationState(
  input: ResolveTerminalThreadCreationStateInput,
): TerminalThreadCreationState {
  const hasExplicitEnvModeOverride =
    input.options !== undefined && Object.hasOwn(input.options, "envMode");
  const explicitEnvMode: DraftThreadEnvMode | undefined = hasExplicitEnvModeOverride
    ? (input.options?.envMode ?? "local")
    : undefined;
  const inheritedEnvMode =
    input.draftThread?.envMode !== undefined
      ? input.draftThread.envMode
      : input.activeThread?.projectId === input.projectId
        ? input.activeThread.envMode
        : input.activeDraftThread?.projectId === input.projectId
          ? input.activeDraftThread.envMode
          : undefined;

  return {
    modelSelection: resolvePreferredComposerModelSelection({
      draft: input.draftComposerState,
      threadModelSelection:
        input.activeThread?.projectId === input.projectId
          ? input.activeThread.modelSelection
          : null,
      projectModelSelection: input.projectDefaultModelSelection,
      userDefaultModelSelection: input.userDefaultModelSelection,
      defaultProvider: input.defaultProvider,
      policy: input.modelSelectionPolicy,
    }),
    runtimeMode:
      input.draftThread?.runtimeMode ??
      (input.activeThread?.projectId === input.projectId ? input.activeThread.runtimeMode : null) ??
      (input.activeDraftThread?.projectId === input.projectId
        ? input.activeDraftThread.runtimeMode
        : null) ??
      DEFAULT_RUNTIME_MODE,
    interactionMode:
      input.projectPlanModeByDefault === true
        ? "plan"
        : (input.draftThread?.interactionMode ??
          (input.activeThread?.projectId === input.projectId
            ? input.activeThread.interactionMode
            : null) ??
          DEFAULT_INTERACTION_MODE),
    envMode: hasExplicitEnvModeOverride
      ? (explicitEnvMode ?? "local")
      : (inheritedEnvMode ?? "local"),
    branch:
      input.options?.branch !== undefined
        ? (input.options.branch ?? null)
        : (input.draftThread?.branch ?? null),
    worktreePath: (() => {
      if (input.options?.worktreePath !== undefined) {
        return input.options.worktreePath ?? null;
      }
      if (explicitEnvMode === "local") {
        return null;
      }
      return input.draftThread?.worktreePath ?? null;
    })(),
  };
}
